import { type JSX, useState } from "react"
import { quickstartWorkspace } from "./api"
import { type ContentView } from "./types"

// The one-time launch form: confirm the objective, pick how many agents and
// what mode they run in, then optionally name them. Submitting creates the
// agents server-side and stamps the workspace as quickstarted, after which
// this pane is no longer offered.

const MAX_AGENTS = 4
const MIN_AGENTS = 1

const MODES = [
  { id: "math", label: "Math" },
  { id: "science", label: "Science" },
  { id: "general", label: "General" },
] as const

type Mode = (typeof MODES)[number]["id"]

function defaultAgentNames(): string[] {
  return Array.from({ length: MAX_AGENTS }, (_unused, index) => `Agent ${index + 1}`)
}

export function QuickStart({
  objective,
  quickstartUrl,
  onOpen,
  onQuickstarted,
}: {
  objective: string
  quickstartUrl: string
  onOpen: (view: ContentView) => void
  onQuickstarted: () => void
}): JSX.Element {
  const [confirmed, setConfirmed] = useState(false)
  const [agentCount, setAgentCount] = useState(MIN_AGENTS)
  const [mode, setMode] = useState<Mode>("general")
  // Kept at full length so lowering the count and raising it again doesn't
  // throw away a name you already edited.
  const [names, setNames] = useState<string[]>(defaultAgentNames)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)

  // Nothing to confirm until the workspace has an objective.
  const hasObjective = objective.trim().length > 0

  function renameAgent(index: number, value: string): void {
    setNames((current) => current.map((name, position) => (position === index ? value : name)))
  }

  async function launch(): Promise<void> {
    if (saving) return
    setSaving(true)
    setFailed(false)

    const created = await quickstartWorkspace(quickstartUrl, names.slice(0, agentCount))
    setSaving(false)
    if (created) onQuickstarted()
    else setFailed(true)
  }

  return (
    <article className="workspace__doc">
      <header className="workspace__doc-head">
        <h1 className="workspace__doc-title">Quick start</h1>
        <p className="workspace__doc-meta">Point a few agents at this workspace's objective.</p>
      </header>

      <form
        className="workspace__form"
        onSubmit={(event) => {
          event.preventDefault()
          void launch()
        }}
      >
        {/* 1 — objective */}
        <section className="workspace__field">
          <label className="workspace__check">
            <input
              type="checkbox"
              checked={confirmed && hasObjective}
              disabled={!hasObjective}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            <span>
              <span className="workspace__check-label">Confirm the objective</span>
              <span className="workspace__field-hint">
                {hasObjective ? "Every agent's overall objective. You can update this later." : "This workspace has no objective yet."}{" "}
                <button
                  type="button"
                  className="workspace__inline-link"
                  onClick={() => onOpen({ kind: "objective" })}
                >
                  View objective
                </button>
                .
              </span>
            </span>
          </label>
        </section>

        {/* 2 — how many agents */}
        <section className="workspace__field">
          <p className="workspace__field-label" id="agent-count-label">
            Agents
          </p>
          <div className="workspace__segmented" role="group" aria-labelledby="agent-count-label">
            {Array.from({ length: MAX_AGENTS }, (_unused, index) => index + MIN_AGENTS).map((count) => (
              <button
                key={count}
                type="button"
                className={`workspace__segment${agentCount === count ? " is-active" : ""}`}
                aria-pressed={agentCount === count}
                onClick={() => setAgentCount(count)}
              >
                {count}
              </button>
            ))}
          </div>
        </section>

        {/* 3 — mode */}
        <section className="workspace__field">
          <p className="workspace__field-label" id="mode-label">
            Mode
          </p>
          <div className="workspace__segmented" role="group" aria-labelledby="mode-label">
            {MODES.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`workspace__segment${mode === option.id ? " is-active" : ""}`}
                aria-pressed={mode === option.id}
                onClick={() => setMode(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </section>

        {/* 4 — advanced */}
        <details className="workspace__advanced">
          <summary className="workspace__advanced-summary">
            <svg className="workspace__chevron" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Advanced settings
          </summary>

          <div className="workspace__advanced-body">
            <div className="workspace__field">
              <p className="workspace__field-label">Agent names</p>
              <div className="workspace__agent-names">
                {names.slice(0, agentCount).map((name, index) => (
                  <input
                    key={index}
                    type="text"
                    className="workspace__input"
                    aria-label={`Name for agent ${index + 1}`}
                    value={name}
                    maxLength={60}
                    onChange={(event) => renameAgent(index, event.target.value)}
                  />
                ))}
              </div>
            </div>
          </div>
        </details>

        <footer className="workspace__form-foot">
          <button type="submit" className="btn btn--primary" disabled={!confirmed || !hasObjective || saving}>
            {saving ? "Starting…" : `Start ${agentCount === 1 ? "agent" : `${agentCount} agents`}`}
          </button>
          <span className="workspace__field-hint">
            {failed
              ? "Could not start the workspace. Try again."
              : !hasObjective
                ? "Set an objective before starting agents."
                : confirmed
                  ? ""
                  : "Confirm the objective first."}
          </span>
        </footer>
      </form>
    </article>
  )
}
